import React from 'react';
import styled from 'styled-components';
import { FlexBox, IActiveTabDetails, TabItem, TabsPanel } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';
import { useGetApplicationListQuery, useGetNotificationListQuery } from 'modules/shared/queries';
import {
    AppListContainer, AuthenticationSettingsContainer, CompanyDetailsContainer, MenuOptionsContainer,
    NotificationsListContainer, StylingContainer, UploadImagesContainer, PushNotificationsContainer
} from '../containers';
import { DefaultAppSettingsTabHelplink } from 'common';
import { IHelpLinks } from 'types';
import { useConfigScopeSelectors } from 'modules/core/scopes';

const StyledTabsPanel = styled(TabsPanel)`
    background-color: ${({ theme: { semantics: { palette } } }) => palette.white};
    padding: 30px 30px 20px 30px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    border-radius: ${(props)=>props.theme.semantics.borderRadius.curvedLarge};
    .ap-tabItem-header{
        font-weight: 500;
    }
    .ap-tabsPanel-tabHeaderContainer {
        height: 30px;
        z-index: 1;
    }
    .ap-tabsPanel-tabContentContainer {
        padding-top: 30px;
        overflow-y: hidden;
        border: none;
    }
    .ap-tabsPanel-tabItemWrapper {
        border-color: ${({ theme: { mobileAppAccelerator: { semanticColors } } }) => semanticColors.genericBorderColor};
    }
    .ap-tabsPanel-tabItemWrapper:first-child {
        border-radius: 6px 0px 0px 6px;
    }
    .ap-tabsPanel-tabItemWrapper:last-child {
        border-radius: 0px 6px 6px 0px;
    }
    .ap-tabsPanel-tabItemWrapper:only-child {
        border-radius: 6px;
    }
`;

const DefaultSettingsContext = React.createContext({
    activeTab: '' as React.Key,
    setTabValue: (value: React.Key) => {
        //NOOP
    },
    defaultSettingsConfig: { doesNotIncludesAppListConfig: false, doesNotIncludesNotificationList: false },
    setDefaultSettingsConfig: (args: { doesNotIncludesAppListConfig: boolean, doesNotIncludesNotificationList: boolean }) => {
        //NOOP
    }
});

const DefaultSettingsProvider = (props: { children?: React.ReactNode }) => {
    const [activeTab, setActiveTab] = React.useState<React.Key>('');
    const [defaultSettingsConfig, setErrorMode] = React.useState({ doesNotIncludesAppListConfig: false, doesNotIncludesNotificationList: false });

    const setTabValue = React.useCallback((value: React.Key) => setActiveTab(value), []);
    const setDefaultSettingsConfig = React.useCallback((value) => setErrorMode(value), []);
    const { data } = useGetApplicationListQuery();
    const { data: notificationData } = useGetNotificationListQuery();

    const effectRenderedRef = React.useRef(true);

    React.useEffect(() => {
        if (data && notificationData && effectRenderedRef.current) {
            const hasNotificationsData = JSON.parse(notificationData.TextData).length > 0;
            const hasAppListData = JSON.parse(data.TextData).length > 0;
            if (!hasNotificationsData || !hasAppListData) {
                setErrorMode({ doesNotIncludesAppListConfig: !hasAppListData, doesNotIncludesNotificationList: !hasNotificationsData });
            }
            effectRenderedRef.current = false; //Used a ref to only reneder this effect once on mount
        }
    }, [data, notificationData]);

    return (
        <DefaultSettingsContext.Provider value={{ activeTab, setTabValue, defaultSettingsConfig, setDefaultSettingsConfig }}>
            {props.children}
        </DefaultSettingsContext.Provider>
    );
};

export const useDefaultSettingsContext = () => React.useContext(DefaultSettingsContext);

const getHelpLinkID = (activeTab: string): keyof IHelpLinks => {
    switch (activeTab) {
        case 'company-details':
            return 'appSettingsCompanyDetails';
        case 'authentication':
            return 'appSettingsAuthentication';
        case 'app-list':
            return 'appSettingsAppList';
        case 'menu-options':
            return 'appSettingsMenuOptions';
        case 'styling':
            return 'appSettingsStyling';
        case 'upload-images':
            return 'appSettingsUploadImages';
        case 'notification-list':
            return 'appSettingsNotificationList';
        case 'push-notifications':
            return 'appSettingsPushNotification';
        default: return 'appSettingsCompanyDetails';
    }
};

const AppSettingsLayoutBase = React.memo(() => {
    const translate = useTranslate();
    const { setTabValue, activeTab } = useDefaultSettingsContext();
    const { selectMobileAppAcceleratorConfig } = useConfigScopeSelectors();
    const { enablePushNotificationConfig } = selectMobileAppAcceleratorConfig();

    const onTabChange = React.useCallback((value: IActiveTabDetails) => {
        setTabValue(value.activeTab);
    }, [setTabValue]);

    return (
        <FlexBox flexDirection='column' width="100%" height="100%">
            <StyledTabsPanel tabWidth={139} tabHeight={30} onChange={onTabChange}>
                <DefaultAppSettingsTabHelplink id={getHelpLinkID(activeTab as string)} />
                <TabItem id="company-details" header={translate('modules.defaultAppSettings.companyDetails.title')}>
                    <CompanyDetailsContainer />
                </TabItem>
                <TabItem id="authentication" header={translate('modules.createNewApp.headers.authentication')}>
                    <AuthenticationSettingsContainer />
                </TabItem>
                <TabItem id="app-list" header={translate('modules.defaultAppSettings.appList.title')}>
                    <AppListContainer />
                </TabItem>
                <TabItem id="menu-options" header={translate('modules.createNewApp.headers.menuOptions')}>
                    <MenuOptionsContainer />
                </TabItem>
                <TabItem id="styling" header={translate('modules.createNewApp.headers.styling')}>
                    <StylingContainer />
                </TabItem>
                <TabItem id="upload-images" header={translate('modules.createNewApp.headers.uploadImages')}>
                    <UploadImagesContainer />
                </TabItem>
                {enablePushNotificationConfig ?
                    <TabItem id="push-notifications" header={'Push Notifications'}>
                        <PushNotificationsContainer />
                    </TabItem> : null}
                <TabItem id="notification-list" header={translate('modules.defaultAppSettings.notificationList.title')}>
                    <NotificationsListContainer />
                </TabItem>
            </StyledTabsPanel>
        </FlexBox>
    );
});

export const AppSettingsLayout = () => (
    <DefaultSettingsProvider>
        <AppSettingsLayoutBase />
    </DefaultSettingsProvider>
);
